import {
    registerApplication,
    start,
    customProps,
    addErrorHandler,
    navigateToUrl,
} from 'single-spa';
import {
    constructApplications,
    constructRoutes,
    constructLayoutEngine,
} from 'single-spa-layout';
import ReactDOM from 'react-dom';
import { createBrowserHistory } from 'history';
import appLayout from './layout.html';
import AlliAuth from '@alli/frontend-auth';
import {
    currentUser,
    currentApplication,
    currentClient,
    setAlliLayout,
    initMonitoring,
    initFeatureFlagging,
} from '@alli/frontend-common';

const unsubscribeMonitoring = initMonitoring();
const unsubscribeFlagging = initFeatureFlagging();

window.addEventListener('beforeunload', () => {
    if(typeof unsubscribeMonitoring === 'function') {
        unsubscribeMonitoring();
    }
    if(typeof unsubscribeFlagging === 'function') {
        unsubscribeFlagging();
    }
});

// this is the ui library as a utility app, not part of the layout at all
registerApplication({
    name: '@agencypmg/alli',
    app: () => System.import('@agencypmg/alli'),
    activeWhen: () => true,
});

registerApplication({
    name: '@agencypmg/alli-design-system',
    app: () => System.import('@agencypmg/alli-design-system'),
    activeWhen: () => true,
});

const history = createBrowserHistory();
const routeData = {
    props: {
        showErrorPage: true,
        contentArea: true,
        collapseSidebar: true,
    },
};
const routes = constructRoutes(appLayout, routeData);
const applications = constructApplications({
    routes,
    loadApp({ name }) {
        return System.import(name);
    },
});
const layoutEngine = constructLayoutEngine({ routes, applications });

applications.forEach(app => {
    const originalApp = app.app;
    app.app = async () => {
        const result = await originalApp();
        // we only want to override and hook in if there's a bootstrap (eg.
        // this app is mounting);
        if (!result.mount) {
            return result;
        }

        if (!Array.isArray(result.mount)) {
            result.mount = [result.mount];
        }

        result.mount.unshift(({ appName, contentArea, collapseSidebar }) => {
            if (contentArea) {
                setAlliLayout({ collapseSidebar });
                window.dispatchEvent(
                    new CustomEvent('alli:content-area-change', {
                        detail: {
                            appName,
                            collapseSidebar,
                        },
                    }),
                );
            }

            return Promise.resolve();
        });

        return result;
    };

    registerApplication({
        ...app,
        customProps: (appName, location) => {
            return {
                ...app.customProps(appName, location),
                appName,
                location,
                history,
            };
        },
    });
});

// this listenes on the current user to kick off an spa render AFTER the current user
// is set by the AlliAuth app rendered below
let spaStarted = false;
let userSub = currentUser.subscribe(user => {
    if (user && !spaStarted) {
        spaStarted = true;
        start({
            urlRerouteOnly: true,
            addErrorHandler: err => {
                console.error(err);
            },
        });

        (window.dataLayer || []).push({
            event: 'alli_user_init',
            user_id: user.id,
            user_email: user.email,
        });

        userSub.unsubscribe();
    }
});

currentApplication.subscribe(app => {
    (window.dataLayer || []).push({
        event: 'alli_application_change',
        current_application: app?.id,
    });
});

currentClient.subscribe(client => {
    if (client) {
        (window.dataLayer || []).push({
            event: 'alli_client_change',
            current_client_id: client.id,
            current_client_slug: client.slug,
        });
    }
});

// this kicks everythign off as the alli auth app will render and go through
// the login flow eventually setting the current user, which calls the subscriber above
ReactDOM.render(
    <AlliAuth document={document} />,
    document.getElementById('auth'),
);
